import { useEffect, useId, useState, useSyncExternalStore } from 'react'
import { ArrowLeft, MessageSquare, MoreHorizontal, Sun, Moon } from 'lucide-react'
import { useStudioStore } from '../../store/studio-store'
import { useTheme } from '../../hooks/use-theme'
import { PersonaSwitcher } from './components/persona-switcher'
import { PublishModal } from '@/components/publish-modal'
import {
  evaluateStudioPublishReadiness,
  withServerDatasourceReadinessProjection,
} from '@/services/project-api'
import { resolveSaveConflictByReload } from '@/store/auto-save-bridge'
import { formatStudioProjectPlanSummary } from '@/features/project/lib/studio-project-plan'
import { ArtifactModeSwitcher } from './components/artifact-mode-switcher'
import { StudioLanguageSwitcher, useStudioLocale } from '@/i18n'
import { CogoLogoBrand } from '@/components/cogo-logo'
import {
  getStudioCapabilityGatewayRevision,
  listPendingStudioCapabilities,
  subscribeStudioCapabilityGateway,
} from '@/features/chat/services/studio-capability-gateway'
import { getCoogoNeedsReviewLabel, getCoogoReviewThenPublishLabel } from './coogo-ide-renewal-copy'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'
import { UndoRedoCluster } from './undo-redo-cluster'
import { humanizePublishReason } from './components/publish-gate-copy'
import { relocalizePublishedReadinessVerdict } from '@/features/validation/publish-readiness-verdict'
import { activateStudioAuthorityTask } from './studio-authority-activation'
import {
  getStudioAuthorityTasks,
  resolveStudioAuthorityDomain,
} from './studio-authority-workspaces'
import { useStudioProjectOutputKind } from './components/artifact-mode-switcher'
import { getProjectProofHealth } from '@/features/project/lib/project-proof-health'

const MOBILE_TOPBAR_MAX_WIDTH = 639

function subscribeMobileTopBar(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener('resize', callback)
  return () => window.removeEventListener('resize', callback)
}

function readMobileTopBar(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_TOPBAR_MAX_WIDTH
}

export function TopBar() {
  const { locale, strings } = useStudioLocale()
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  useEffect(() => {
    relocalizePublishedReadinessVerdict(locale)
  }, [locale])
  const mobileTopBar = useSyncExternalStore(
    subscribeMobileTopBar,
    readMobileTopBar,
    () => false,
  )
  useSyncExternalStore(
    subscribeStudioCapabilityGateway,
    getStudioCapabilityGatewayRevision,
    getStudioCapabilityGatewayRevision,
  )
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const currentProjectName = useStudioStore((s) => s.currentProjectName)
  const currentProject = useStudioStore((s) => s.currentProject)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const projectPlan = useStudioStore((s) => s.projectPlan)
  const projectSource = useStudioStore((s) => s.projectSource)
  const isDirty = useStudioStore((s) => s.isDirty)
  const isSaving = useStudioStore((s) => s.isSaving)
  const documentWorkspaceSaveCounts = useStudioStore(
    (s) => s.documentWorkspaceSaveCounts,
  )
  const saveError = useStudioStore((s) => s.saveError)
  const saveConflict = useStudioStore((s) => s.saveConflict)
  const isResolvingConflict = useStudioStore((s) => s.isResolvingConflict)
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen)
  const projectLoadError = useStudioStore((s) => s.projectLoadError)
  const chatContext = useStudioStore((s) => s.chatContext)
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const toggleChatSidebar = useStudioStore((s) => s.toggleChatSidebar)
  const closeProject = useStudioStore((s) => s.closeProject)
  const developer = useStudioStore((s) => s.studioPersona === 'developer')
  const projectName = currentProject?.manifest?.name ?? projectMeta?.name ?? currentProjectName ?? 'Studio'
  const isDocumentWorkspaceSaving = currentProjectId != null && (documentWorkspaceSaveCounts[currentProjectId] ?? 0) > 0
  const projectProofHealth = getProjectProofHealth(
    { locale, strings },
    currentProject,
    projectMeta,
  )
  const hasVerifiedLiveReadback = projectProofHealth.status === 'verified'

  const { resolvedTheme, setTheme } = useTheme()
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const publishReasonId = useId()
  const pendingReviewCount = listPendingStudioCapabilities().length
  const publishLabel =
    pendingReviewCount > 0 ? getCoogoReviewThenPublishLabel(locale, pendingReviewCount) : strings.chrome.publish

  const projectSlug = projectMeta?.slug
  const readinessProject = withServerDatasourceReadinessProjection(
    currentProject,
    projectMeta,
    currentProjectId,
  )
  // Single source of truth: the same readiness gate the Publish surface
  // uses. The old predicate (id && slug) let the TopBar button look
  // functional while the gate was failing.
  const publishGate = currentProjectId
    ? evaluateStudioPublishReadiness(
        readinessProject as never,
        projectPlan ?? projectMeta?.studioProjectPlan ?? null,
        currentProjectId,
      )
    : null
  // A failed project load outranks the readiness gate (F13, 2026-08-13).
  //
  // Measured: `openServerProject` leaves `currentProjectId`, `projectMeta` and
  // the slug on the PREVIOUS project when a load fails, so after a failed open
  // from either in-app project picker (selection-context-panel.tsx:149,
  // chat-messages.tsx:284) this gate still passed — Publish read "Publish
  // current project", was enabled, and opened the publication-candidate dialog
  // for a project the user was no longer looking at, behind a screen saying the
  // project could not be loaded. (On a FRESH load of a missing project the id
  // is null, so the button was already inert — see the report; the reachable
  // hazard is the stale-id one.) Whatever the store still holds, a load
  // failure is not a state from which anything may be published.
  const canPublish = Boolean(
    currentProjectId && projectSlug && publishGate?.publishReady && !projectLoadError,
  )
  const firstBlockedReason = publishGate?.reasons[0]
  const blockedReasonTitle = firstBlockedReason
    ? humanizePublishReason(firstBlockedReason, locale).title
    : chromeCopy.readinessGateFailed
  const publishTitle = projectLoadError
    ? chromeCopy.publishNeedsLoadedProject
    : !currentProjectId || !projectSlug
      ? strings.chrome.openProjectToPublish
      : publishGate && !publishGate.publishReady
        ? `${strings.chrome.publishBlocked}: ${blockedReasonTitle}`
        : strings.chrome.publishCurrent

  // The readiness gate authors THREE things per blocked reason — a title, a
  // plain-language `detail`, and a resolution `action` (publish-gate-copy.ts).
  // This control could only ever surface the title, in `title=`. The detail and
  // the ask-Cogo button render in PublishGateRows, which mounts on the Publish
  // surface — so the operator was told *why* on hover and never *what to do*.
  //
  // Measured on the blocked overview surface (creator persona, ko, 1280px):
  // the detail string is absent from the DOM entirely, the title exists only
  // inside the `title=` attribute, and for creators the Publish rail entry sits
  // behind a collapsed "더 보기" disclosure while the rail's own recommendation
  // points back at overview. A route existed; no cue did.
  //
  // Only the GATE-blocked case has somewhere to send the operator. "No project
  // open" and "project failed to load" have no publish surface worth showing,
  // so they stay inert rather than routing somewhere that cannot explain
  // itself.
  const outputKind = useStudioProjectOutputKind()
  const authorityDomain =
    chatContext?.authorityDomain ??
    resolveStudioAuthorityDomain(activeRailItem) ??
    'primary'
  // `findStudioAuthorityTask` falls back to `tasks[0]` for an unknown view, so
  // asking it would silently route to Overview for output kinds that have no
  // publish task (e.g. `document`). Ask the catalog directly instead.
  const publishTaskExists = getStudioAuthorityTasks(
    authorityDomain,
    outputKind,
  ).some((task) => task.id === 'publish')
  const publishGateBlocked = Boolean(
    currentProjectId &&
      projectSlug &&
      !projectLoadError &&
      publishGate &&
      !publishGate.publishReady &&
      publishTaskExists,
  )
  const handlePublishActivate = () => {
    if (canPublish) {
      setPublishModalOpen(true)
      return
    }
    if (publishGateBlocked) {
      activateStudioAuthorityTask({
        domain: authorityDomain,
        outputKind,
        requestedView: 'publish',
      })
    }
  }

  // State-derived project status — replaces the hardcoded always-on LIVE
  // pill (a truthful-status violation: it claimed live regardless of
  // save/publish state). "Live" appears only for a published project.
  const projectStatus = !currentProjectId
    ? null
    : saveConflict
      ? // F11 — an unresolved optimistic-concurrency conflict outranks every
        // other state: the edit was halted (not retried), so neither "Saved"
        // nor "Save failed" would be truthful.
        {
          label: strings.chrome.conflict,
          className: "border-destructive/25 bg-destructive/10 text-destructive",
          dotClassName: "bg-destructive",
        }
      : saveError
        ? {
            label: strings.chrome.saveFailed,
            className:
              "border-destructive/25 bg-destructive/10 text-destructive",
            dotClassName: "bg-destructive",
          }
      : isSaving || isDocumentWorkspaceSaving
          ? {
              label: strings.chrome.saving,
              className: "border-border bg-muted text-muted-foreground",
              dotClassName: "bg-muted-foreground/60",
            }
          : isDirty
            ? {
                label: strings.chrome.unsaved,
                className: "border-border bg-muted text-muted-foreground",
                dotClassName: "bg-muted-foreground/60",
              }
            : projectMeta?.status === "published" && hasVerifiedLiveReadback
              ? {
                  label: strings.chrome.live,
                  className: 'border-success/20 bg-success/10 text-success',
                  dotClassName: 'bg-success',
                }
              : projectSource === "server"
                ? {
                    label: strings.chrome.saved,
                    className: 'border-success/20 bg-success/10 text-success',
                    dotClassName: 'bg-success',
                  }
                : {
                    label: strings.chrome.draft,
                    className: "border-border bg-muted text-muted-foreground",
                    dotClassName: "bg-muted-foreground/60",
                  }
  const projectPlanSummary =
    developer && currentProjectId
      ? formatStudioProjectPlanSummary(
          projectPlan ?? projectMeta?.studioProjectPlan,
          projectMeta?.app_target,
          locale,
        )
      : null
  const handleBackToProjects = () => {
    if (currentProjectId) closeProject()
    window.history.pushState(null, '', '/studio')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  return (
    <>
      <div
        data-testid="topbar"
        data-mobile-layout={mobileTopBar ? "true" : "false"}
        className={`relative z-40 grid h-[46px] min-w-0 items-center border-b border-border bg-card text-sm ${
          mobileTopBar
            ? "grid-cols-[auto_minmax(0,1fr)_auto] gap-1 overflow-visible px-2"
            : "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 overflow-hidden px-3.5"
        }`}
      >
        <div data-testid="topbar-project-context" className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={handleBackToProjects}
            aria-label={chromeCopy.backToProjects}
            className={`flex shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              mobileTopBar ? "h-11 w-11" : "h-7 w-7"
            }`}
          >
            <ArrowLeft className="h-[17px] w-[17px]" strokeWidth={1.8} />
          </button>
          {!mobileTopBar ? (
            <>
              <CogoLogoBrand size={32} />
              <span
                data-testid="topbar-project-name"
                className="max-w-48 truncate text-[13.5px] font-bold text-foreground"
                title={projectName}
              >
                {projectName}
              </span>
            </>
          ) : null}
          {projectPlanSummary && (
            <span
              data-testid="topbar-project-plan"
              className="hidden max-w-[180px] shrink-0 truncate rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground xl:inline-flex"
              title={projectPlanSummary}
            >
              {projectPlanSummary}
            </span>
          )}
          <div className="hidden lg:block">
            <PersonaSwitcher />
          </div>

          {saveConflict && (
            <div
              data-testid="save-conflict-banner"
              role="alert"
              className="hidden min-w-0 shrink items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive xl:flex"
            >
              <span className="truncate">{strings.chrome.reloadConflict}</span>
              <button
                type="button"
                data-testid="save-conflict-reload"
                onClick={() => {
                  void resolveSaveConflictByReload()
                }}
                disabled={isResolvingConflict}
                aria-busy={isResolvingConflict}
                className="shrink-0 rounded border border-destructive/40 px-1.5 py-0.5 font-semibold hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isResolvingConflict
                  ? strings.chrome.reloading
                  : strings.chrome.reload}
              </button>
            </div>
          )}
        </div>

        <ArtifactModeSwitcher compact={mobileTopBar} />

        {/* Right cluster */}
        <div className="flex min-w-0 items-center justify-end gap-1.5">
          {pendingReviewCount > 0 && (
            <span
              data-testid="topbar-needs-review"
              className="hidden shrink-0 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 lg:inline-flex dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
            >
              {getCoogoNeedsReviewLabel(locale, pendingReviewCount)}
            </span>
          )}
          {projectStatus && (
            <span
              data-testid="topbar-project-status"
              className={`hidden shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold lg:inline-flex ${projectStatus.className}`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${projectStatus.dotClassName}`}
                aria-hidden
              />
              {projectStatus.label}
            </span>
          )}
          {!mobileTopBar ? <UndoRedoCluster /> : null}
          <button
            type="button"
            aria-label={strings.chrome.toggleIntentLoop}
            aria-expanded={chatSidebarOpen}
            aria-controls="chat-responsive-panel"
            title={strings.chrome.toggleIntentLoop}
            onClick={() => {
              const opening = !chatSidebarOpen
              toggleChatSidebar?.()
              if (opening) {
                window.setTimeout(() => {
                  window.dispatchEvent(new CustomEvent("cogo:focus-chat-input"))
                }, 0)
              }
            }}
            className={`inline-flex items-center justify-center gap-1.5 rounded-md border text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              mobileTopBar ? "h-11 min-w-11 px-2" : "h-9 min-w-9 px-2"
            } ${
              chatSidebarOpen
                ? "border-border/70 bg-muted text-foreground"
                : "bg-card text-foreground border-border hover:bg-accent"
            }`}
          >
            <MessageSquare size={16} strokeWidth={1.7} />
            <span className="hidden xl:inline">{chromeCopy.chat}</span>
          </button>

          {mobileTopBar ? (
            <details data-testid="topbar-mobile-more" className="group relative">
              <summary
                role="button"
                aria-label={strings.chrome.moreControls}
                className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden"
              >
                <MoreHorizontal size={18} strokeWidth={1.7} aria-hidden />
              </summary>
              <div className="absolute right-0 top-[calc(100%+0.375rem)] z-50 w-56 rounded-lg border border-border bg-card p-2 shadow-lg">
                <div className="flex min-h-11 items-center justify-between gap-3 px-1 text-xs font-medium text-foreground [&_button]:h-11 [&_button]:w-11">
                  <span>{strings.chrome.history}</span>
                  <UndoRedoCluster />
                </div>
                <button
                  type="button"
                  aria-label={
                    resolvedTheme === "dark"
                      ? strings.chrome.lightMode
                      : strings.chrome.darkMode
                  }
                  onClick={() =>
                    setTheme(resolvedTheme === "dark" ? "light" : "dark")
                  }
                  className="flex h-11 w-full items-center gap-2 rounded-md px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {resolvedTheme === "dark" ? (
                    <Sun size={16} strokeWidth={1.7} aria-hidden />
                  ) : (
                    <Moon size={16} strokeWidth={1.7} aria-hidden />
                  )}
                  {resolvedTheme === "dark"
                    ? strings.chrome.lightMode
                    : strings.chrome.darkMode}
                </button>
                <div className="flex min-h-11 items-center justify-between gap-3 rounded-md px-3 text-xs font-medium text-foreground">
                  <span>{strings.language.label}</span>
                  <StudioLanguageSwitcher compact />
                </div>
              </div>
            </details>
          ) : (
            <>
              <button
                type="button"
                aria-label={
                  resolvedTheme === "dark"
                    ? strings.chrome.lightMode
                    : strings.chrome.darkMode
                }
                onClick={() =>
                  setTheme(resolvedTheme === "dark" ? "light" : "dark")
                }
                className="hidden border border-border bg-card p-1 text-foreground transition-colors hover:bg-accent lg:inline-flex lg:rounded-md"
              >
                {resolvedTheme === "dark" ? (
                  <Sun size={16} strokeWidth={1.7} />
                ) : (
                  <Moon size={16} strokeWidth={1.7} />
                )}
              </button>
              <StudioLanguageSwitcher compact />
            </>
          )}

          <div
            className="mx-1 hidden h-5 w-px bg-border lg:block"
            aria-hidden
          />

          {/*
            A11Y-01 (GroupedRail's ContextualRailButton took the same decision
            for the same reason): a natively `disabled` button is removed from
            the tab order, so the explanation attached to it is unreachable to
            exactly the audience the explanation exists for. A `title=` tooltip
            additionally never fires on touch. Both were true here — the blocked
            reason was keyboard- and touch-invisible.

            `aria-disabled` keeps the control focusable and its reason
            announceable while still telling assistive tech the publish action
            is unavailable; the disabled styling is retained explicitly since
            `:disabled` no longer matches. Activation is gated in
            `handlePublishActivate`, so a blocked project still cannot publish.
          */}
          <button
            type="button"
            data-testid="topbar-publish"
            aria-label={publishLabel}
            aria-disabled={!canPublish || undefined}
            aria-describedby={!canPublish ? publishReasonId : undefined}
            title={publishTitle}
            onClick={handlePublishActivate}
            className={`hidden h-7 items-center rounded-lg px-3.5 text-[12px] font-semibold transition-colors lg:inline-flex ${
              canPublish
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : publishGateBlocked
                  ? 'cursor-pointer bg-muted text-muted-foreground hover:bg-muted/80'
                  : 'cursor-not-allowed bg-muted text-muted-foreground'
            }`}
          >
            {publishLabel}
          </button>
          {!canPublish && (
            <span id={publishReasonId} className="sr-only">
              {publishGateBlocked
                ? `${publishTitle} — ${chromeCopy.publishBlockedGuidance}`
                : publishTitle}
            </span>
          )}
        </div>
      </div>
      {publishModalOpen && canPublish && currentProjectId && projectSlug && (
        <PublishModal
          projectId={currentProjectId}
          slug={projectSlug}
          currentVersion={projectMeta?.version ?? 0}
          onClose={() => setPublishModalOpen(false)}
          onPublished={() => undefined}
        />
      )}
    </>
  )
}
